import { useState, useRef, useEffect } from 'preact/hooks';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-preact';

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: preact.ComponentChildren;
}

interface SelectTriggerProps {
  className?: string;
  children: preact.ComponentChildren;
}

interface SelectContentProps {
  children: preact.ComponentChildren;
}

interface SelectItemProps {
  value: string;
  children: preact.ComponentChildren;
}

export function Select({ value, onValueChange, children }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [selectedLabel, setSelectedLabel] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleItemClick = (itemValue: string, itemLabel: string) => {
    setSelectedValue(itemValue);
    setSelectedLabel(itemLabel);
    onValueChange(itemValue);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={triggerRef}>
      {children}
    </div>
  );
}

export function SelectTrigger({ className, children }: SelectTriggerProps) {
  return (
    <div
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </div>
  );
}

export function SelectValue({ placeholder }: { placeholder?: string }) {
  return <span className="text-muted-foreground">{placeholder}</span>;
}

export function SelectContent({ children }: SelectContentProps) {
  return (
    <div className="relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
      <div className="p-1">
        {children}
      </div>
    </div>
  );
}

export function SelectItem({ value, children }: SelectItemProps) {
  return (
    <div
      className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-accent hover:text-accent-foreground"
    >
      {children}
    </div>
  );
}